import { get } from 'lodash';
import moment from 'moment';
import {
  getIdsFromProducts as pertimmGetIdsFromProducts,
  searchWithFacets as pertimmSearchWithFacets,
} from 'Utils/pertimm-client';
import resortThumbnailQuery, {
  getPriceFields,
  cleanupArgs,
} from 'Utils/resort-thumbnail-query/resortThumbnailQuery';
import gqlClient from 'Utils/graphql-client/graphqlClient';
import {
  STATE_SEARCH_OFFSET_SELL,
  STATE_SEARCH_PRODUCT_CONFIG,
  STATE_SEARCH_PRODUCTS,
  STATE_SEARCH_PRODUCT_PRICE,
  STATE_SEARCH_PRODUCT_CHILDCARE,
  STATE_SEARCH_FACETING_TOP_COUNTRIES,
} from 'Utils/request/requestDictionary';
import { getKidAgeInMonths } from 'Utils/faceting/facetingUtils';
import {
  DEPARTURE_CITY_FACET_ID,
  DATE_FORMAT,
  CHILDCARE_FILTER_FACET_AVERAGE_MONTHS,
} from 'Utils/faceting/constants';
import { API_DATE_FORMAT } from 'Utils/date/apiDate';
import { formatTimestampDate } from 'Utils/date/dateUtils';
import { isSeller } from 'Utils/seller/sellerUtils';
import { getFacetLocalizedLabelKey } from 'Components/widgets/criteria/criteria-facets/search-facets/SearchFacets.utils';
const PERTIMM_ENGLISH_SPEAKING_LOCALE = 'en';
export function getPertimmDateFormat(locale?: string): string {
  return locale?.slice(0, 2) === PERTIMM_ENGLISH_SPEAKING_LOCALE ? 'YYYY/MM/DD' : 'DD/MM/YYYY';
}
export function formatPertimmDate(departureDate: moment, locale: string): string {
  const format = getPertimmDateFormat(locale);
  return departureDate.isValid() ? departureDate.format(format) : '';
}

function getResortThumbnailArgsConfig(appContext: AppContext): Promise<any> {
  const query = `query {
    config {
      thumbnails {
        tourDurationPlaceholder
        cruiseTimingPlaceholder
        cruiseDurationPlaceholder
        dateFormat
      }
    }
  }`;
  return gqlClient.runQuery(query, appContext, {
    requestName: STATE_SEARCH_PRODUCT_CONFIG,
  });
}

type GetAllProductsInfoParams = {
  appContext: AppContext;
  options?: FacetingSearchOptions;
  ids?: Array<string>;
};

function getAllProductsInfo({ appContext, options = {}, ids = [] }: GetAllProductsInfoParams) {
  const { cmsContent, cmsRelease } = appContext;
  const { offerId = '', offerName = '', productIds = [] } = options;
  return getResortThumbnailArgsConfig(appContext).then((data) => {
    const resortThumbnailQueryArgs = {
      content: cmsContent,
      release: cmsRelease,
      isMice: false,
      offerName,
      offerId,
      ...data.config.thumbnails,
    };
    const allProductsId = productIds.length ? ids.filter((id) => productIds.includes(id)) : ids;

    if (!allProductsId.length) {
      return Promise.resolve({
        resorts: [],
      });
    }

    const query = `query {
      resorts(resortIds : ["${allProductsId.join('","')}"]) {
        ${resortThumbnailQuery(resortThumbnailQueryArgs, {
          withoutPrice: true,
        })}
      }
    }`;
    return gqlClient.runQuery(query, appContext, {
      requestName: STATE_SEARCH_PRODUCTS,
    });
  });
}

type LoadCMSFacetingTopCountriesParams = {
  appContext: AppContext;
};
export function loadCMSFacetingTopCountries({ appContext }: LoadCMSFacetingTopCountriesParams) {
  const query = `query {
    config {
      faceting {
        topCountries {
          enabled
           title
           items {
             id
           }
        }
      }
    }
  }`;
  return gqlClient.runQuery(query, appContext, {
    requestName: STATE_SEARCH_FACETING_TOP_COUNTRIES,
  });
}
type LoadOffsetSellParams = {
  appContext: AppContext;
};
export function loadOffsetSell({ appContext }: LoadOffsetSellParams) {
  const query = `
    query {
      locale {
        joyviewOffsetSell
        resortOffsetSell
      }
    }
  `;
  return gqlClient.runQuery(query, appContext, {
    requestName: STATE_SEARCH_OFFSET_SELL,
  });
}
type LoadProductsParams = {
  appContext: AppContext;
  options?: FacetingSearchOptions;
};
export function loadProducts({ appContext, options }: LoadProductsParams) {
  return pertimmGetIdsFromProducts(appContext).then((ids) => {
    return getAllProductsInfo({
      appContext,
      options,
      ids,
    });
  });
}
type LoadProductsWithIdsParams = {
  appContext: AppContext;
  productIds: Array<string>;
};
export function loadProductsWithIds({ appContext, productIds }: LoadProductsWithIdsParams) {
  return getAllProductsInfo({
    appContext,
    options: {},
    ids: productIds,
  });
}
type SearchWithFacetsParams = {
  criteria: FacetingSearchCriteria;
  options: FacetingSearchOptions;
  appContext: AppContext;
};
export function searchWithFacets({
  criteria,
  options,
  appContext,
}: SearchWithFacetsParams): Promise<any> {
  const facetingTopCountriesPromise = loadCMSFacetingTopCountries({
    appContext,
  });
  const productsPromise = loadProducts({
    appContext,
    options,
  });
  const userIsSeller = isSeller(appContext);
  const searchPromise = pertimmSearchWithFacets({
    criteria: { ...criteria, tripDuration: userIsSeller ? null : criteria.tripDuration },
    appContext,
  });
  return Promise.all([facetingTopCountriesPromise, productsPromise, searchPromise]).then(
    ([facetingTopCountriesInfo, productsInfo, pertimmResults]) => {
      const facetingTopCountries = get(facetingTopCountriesInfo, 'config.faceting.topCountries');
      const filteredProducts = get(pertimmResults, 'answers', []).reduce((acc, result) => {
        const resultProductId = get(result, 'meta.product_id[0]');
        const resort = productsInfo.resorts.find(({ id }) => id === resultProductId);
        const isAllAttendeeAllowed =
          !!resort &&
          (!criteria.kids ||
            criteria.kids.every(
              ({ birthdate }) =>
                getKidAgeInMonths({
                  birthdate,
                  departureDate: criteria.departureDate,
                }) >= resort.minAuthorizedAge.value,
            ));
        return resort && isAllAttendeeAllowed ? [...acc, resort] : acc;
      }, []);
      const sortedFilteredProducts =
        criteria.productIds && criteria.productIds.length > 0
          ? criteria.productIds.map((id) => filteredProducts.find((product) => product.id === id))
          : filteredProducts;
      return {
        facetingTopCountries,
        ...pertimmResults,
        query: criteria.query || '',
        search: {
          products: sortedFilteredProducts.filter(Boolean),
        },
      };
    },
  );
}
type TransportArgs = {
  departureCity: string;
  forceWithoutTransport: boolean;
};

function getTransportArgs(criteria): TransportArgs {
  const departureCityFilter = get(criteria, 'filters', []).find(
    (filter) => filter.id === DEPARTURE_CITY_FACET_ID,
  );
  const selectedDepartureCity = get(departureCityFilter, 'name');

  switch (selectedDepartureCity) {
    case 'NO':
      return {
        forceWithoutTransport: true,
        departureCity: '',
      };

    case undefined:
      return {
        forceWithoutTransport: false,
        departureCity: criteria.defaultDepartureCity || '',
      };

    default:
      return {
        forceWithoutTransport: false,
        departureCity: selectedDepartureCity,
      };
  }
}

export type FacetingPricesCriteria = {
  boutique?: string;
  defaultDepartureCity?: string;
  departureDate: number | null | undefined;
  duration: number;
  filters: Array<{
    id: string;
    name: string;
  }>;
  maxDate?: number;
  minDate?: number;
  offerId?: string;
  offerName?: string;
  productIds: Array<string>;
  query: string;
};
type LoadPricesParams = {
  appContext: AppContext;
  criteria: FacetingPricesCriteria;
  withFlexibility: boolean;
};
export function loadPrices({ appContext, criteria = {}, withFlexibility }: LoadPricesParams) {
  const { offerName = '', offerId = '', productIds = [], duration } = criteria;
  const { departureCity, forceWithoutTransport } = getTransportArgs(criteria);
  const date = formatTimestampDate(criteria.departureDate, DATE_FORMAT) || '';
  const dateStart = formatTimestampDate(criteria.minDate, DATE_FORMAT) || '';
  const dateEnd = formatTimestampDate(criteria.maxDate, DATE_FORMAT) || '';
  const hasDateRange = dateStart && dateEnd && (!date || withFlexibility);
  const query = `
    query {
      resorts(
        optimizedPriceCalls: true,
        ${date ? `date: "${date}",` : ''}
        ${hasDateRange ? `dateStart: "${dateStart}",` : ''}
        ${hasDateRange ? `dateEnd: "${dateEnd}",` : ''}
        resortIds: ["${productIds.join('","')}"],
        departureCity: "${departureCity}",
        duration: ${String(duration || null)},
        offerName: "${offerName}",
        offerId: "${offerId}",
        forceWithoutTransport: ${String(forceWithoutTransport)},
      ) {
        id
        ${getPriceFields(
          cleanupArgs({
            isMice: false,
            optimizedPriceCalls: true,
            resortIds: productIds,
            departureCity,
            duration: duration || undefined,
            // for flow: criteria duration can be null, but cleanupArgs doesn't accept null as parameter.
            offerName,
            offerId,
            forceWithoutTransport,
            content: appContext.cmsContent,
            release: appContext.cmsRelease,
            date,
            dateStart,
            dateEnd,
          }),
        )}
      }
    }
  `;
  return gqlClient.runQuery(query, appContext, {
    requestName: STATE_SEARCH_PRODUCT_PRICE,
  });
}
export type FacetingChildcareCriteria = {
  childcareActiveFilters: Array<string>;
  departureDate: number;
  duration: number;
  kids: Array<{
    birthdate?: timestamp;
    childcareIncluded: boolean;
  }>;
  productIds: Array<string>;
};
type LoadChildcareParams = {
  appContext: AppContext;
  criteria: FacetingChildcareCriteria;
};
export function loadChildcareAvailabilities({ appContext, criteria = {} }: LoadChildcareParams) {
  const {
    childcareActiveFilters,
    productIds = [],
    departureDate: arrivalDate,
    duration,
    kids,
  } = criteria;
  let concernedAttendeesBirthdates = [];

  if (kids?.length > 0) {
    concernedAttendeesBirthdates = concernedAttendeesBirthdates.concat(
      kids.map(({ birthdate }) => formatTimestampDate(birthdate, API_DATE_FORMAT)),
    );
  }

  if (childcareActiveFilters?.length > 0 && moment(arrivalDate).isValid()) {
    concernedAttendeesBirthdates = concernedAttendeesBirthdates.concat(
      childcareActiveFilters.map((filterName) =>
        moment(arrivalDate)
          .subtract(
            CHILDCARE_FILTER_FACET_AVERAGE_MONTHS[getFacetLocalizedLabelKey(filterName)],
            'months',
          )
          .format(API_DATE_FORMAT),
      ),
    );
  }

  if (concernedAttendeesBirthdates.length === 0) {
    return Promise.resolve();
  }

  const query = `
    query {
      childcareSearch(
        arrival_date: "${formatTimestampDate(arrivalDate, API_DATE_FORMAT) || ''}",
        product_ids: ["${productIds.join('","')}"],
        concerned_attendees_birthdates: ["${concernedAttendeesBirthdates.join('","')}"],
        duration: ${String(duration || null)},
      ) {
        id
        productId,
        availability,
        isIncluded,
        ageMin,
        ageMax
        remainingStock
        timeSlot
      }
    }
  `;
  return gqlClient.runQuery(query, appContext, {
    requestName: STATE_SEARCH_PRODUCT_CHILDCARE,
  });
}
